import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Field, Form, Formik, FormikErrors } from 'formik';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import {
  InputField,
  HeadingOne,
  HeadingTwo,
  HelpButton,
  ScrollTop,
  SolutionType,
  StyledDatePicker,
  IdentityTypeDirectors,
  useTealiumIdle,
} from 'src/features/common';
import {
  ApplicationActionType,
  FormLayout,
  HeadingLayout,
  PageLayout,
  useWizardEffect,
} from 'src/features/common';
import {
  isUserUnderAgeIdNumber,
  isUnderAge as isUserUnderAge,
  isValidPassportExpiryDate,
} from 'src/features/common/helpers/dates';
import { MIN_DATE_ALLOWED } from 'src/common';
import { ApplicationContext } from '../../context';
import {
  asylumMask,
  asylumRegex,
  idMask,
  passportMask,
  passportRegex,
  phoneNumberMaskSpaced,
  refugeeMask,
  refugeeRegex,
  spaceBy4Mask,
  stripWhitespace,
} from 'src/helpers/formatting';
import {
  isValidEmail,
  isValidID,
  isValidMobileNumber,
  isValidName,
  validateEmailTradeRoot,
} from 'src/helpers/validation';
import {
  ConsumerType,
  Contact,
  MARITAL_STATUSES,
  CREDIT_VETTING_TITLE,
} from 'src/features/common/types/application/contact';
import { Environment } from 'src/Environment';
import { FormRowLayout, MiniAppFocusWrapper } from 'src/components';
import { FormikChangeListener } from 'src/features/order/components/product-quantity-change-listener/product-quantity-change-listener';
import { useAsyncDispatch } from '../../../order/hooks/use-async-dispatch';
import { getApplicationAPIFromApplication } from '../../../common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { ListItem } from '../../../common/types/domain/list-item';
import { PAGE_NAME } from 'src/features/common/types/tealium-service';

import { UnderAgeModal } from 'src/features/common/components/underage-modal/underage-modal';
import { killApplication } from 'src/features/common/reducers/application/application-reducer-helpers';
import { FormStatus, getFormStatus } from 'src/features/onboarding/helpers/fields-status';
import { getPersonalDetailsHeadings } from 'src/features/onboarding/helpers/headings';
import OnboardingTealiumService from '../../services/onboarding-tealium-services';
import { COUNTRY_CODES } from 'src/services/salesforce-service-helpers/country-codes';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { LAST_NAME_PLACEHOLDER, LOCK_NAMES, isLocked } from 'src/helpers/lock-field-mappings';

import './personal-details.scss';

const CONSUMER_TYPE_LIST: ListItem<ConsumerType>[] = [
  { value: ConsumerType.DIRECTOR, label: 'Business owner/director' },
  { value: ConsumerType.ASSISTANT, label: 'Assistant on behalf of the business owner/director' },
];

export const IDENTITY_TYPE_LIST: ListItem<IdentityTypeDirectors>[] = [
  { value: IdentityTypeDirectors.RSA_ID, label: 'South African' },
  { value: IdentityTypeDirectors.PASSPORT, label: 'Passport' },
  { value: IdentityTypeDirectors.REFUGEE, label: 'Refugee' },
  { value: IdentityTypeDirectors.ASYLUM_SEEKER, label: 'Asylum Seeker' },
];

type PersonalDetailsForm = {
  identityType: ListItem<IdentityTypeDirectors>;
  idNumber?: string;
  name?: string;
  surname?: string;
  emailAddress?: string;
  mobileNumber?: string;
  consumerType?: ListItem<ConsumerType>;
  title?: string;
  initials?: string;
  maritalStatus?: string;
  dateOfBirth?: Date;
  passportExpiryDate?: Date;
  countryOfOrigin?: string;
  gender?: string;
};

export const PersonalDetails = () => {
  const { application, dispatch, catalogue, salesforceService } = useContext(ApplicationContext);
  const dispatchAsync = useAsyncDispatch(application, dispatch);
  const isMobile = useIsMobile();
  const { initFieldCount, sendIdleEvent } = useTealiumIdle();

  const [isUnderAge, setUnderAge] = useState(false);

  const isThirdPartyJourney = useMemo(
    () => application.solutionType === SolutionType.thirdPartyOnboarding,
    [application.solutionType],
  );

  const headings = useMemo(
    () => getPersonalDetailsHeadings(isThirdPartyJourney),
    [isThirdPartyJourney],
  );

  const inMiniApp = useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);

  const taggingJourney = useMemo(() => {
    switch (true) {
      case !!application.pendingApp?.isPending:
        return 'pending app';

      case !!application.brokerLogin || application.agentId:
        return 'third party';

      case application.deviceActivationTagging:
        return 'register device';

      default:
        return 'onboarding';
    }
  }, []);

  const FIRST_NAME_MAX_LENGTH = 40;
  const SURNAME_MAX_LENGTH = 40;
  const placeholder =
    application.brokerLogin || application.applicationKilled
      ? 'The customer is the...'
      : 'I am the';
  const pageName = PAGE_NAME.PERSONAL_DETAILS;

  const creditVettingEnabled = useMemo((): boolean => !!application.creditVetting?.required, []);

  const isForeignNationalsEnabled =
    !Environment.current?.disabledFeatures?.passport && !creditVettingEnabled;

  const isAnyFormErrors = (values: PersonalDetailsForm) => {
    const errors = validate(values);
    return Object.values(errors).length > 0;
  };

  const validate = (values: PersonalDetailsForm): FormikErrors<PersonalDetailsForm> => {
    const errors: FormikErrors<PersonalDetailsForm> = {};
    setNextButtonEnabled(Object.keys(errors).length === 0);
    if (!values.consumerType) {
      errors.consumerType = 'The consumer type is invalid';
    }

    if (
      values.identityType.value === IdentityTypeDirectors.RSA_ID &&
      !isValidID(stripWhitespace(values.idNumber))
    ) {
      errors.idNumber = 'The ID number entered is invalid';
    }

    if (
      values.identityType.value === IdentityTypeDirectors.RSA_ID &&
      values.idNumber &&
      isValidID(stripWhitespace(values.idNumber)) &&
      isUserUnderAgeIdNumber(values.idNumber)
    ) {
      setUnderAge(true);
      if (isUnderAge) {
        OnboardingTealiumService.onUnderageModalStart(taggingJourney);
      }
      errors.idNumber = 'The age limit is 18 years or older';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.PASSPORT &&
      values.idNumber &&
      !passportRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Passport number entered is invalid';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.REFUGEE &&
      values.idNumber &&
      !refugeeRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Document ID entered is invalid';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.ASYLUM_SEEKER &&
      values.idNumber &&
      !asylumRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Document ID entered is invalid';
    }

    if (values.identityType.value !== IdentityTypeDirectors.RSA_ID) {
      if (!values.countryOfOrigin) {
        errors.countryOfOrigin = 'Country of origin is invalid';
      }

      if (!values.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is invalid';
      }

      if (values.dateOfBirth && isUserUnderAge(new Date(values.dateOfBirth))) {
        errors.dateOfBirth = 'The age limit is 18 years or older';
        setUnderAge(true);
      }

      if (!values.passportExpiryDate) {
        errors.passportExpiryDate = `${
          values.identityType.value === IdentityTypeDirectors.PASSPORT ? 'Passport' : 'Document'
        } expiry date is invalid`;
      }

      if (values.passportExpiryDate && !isValidPassportExpiryDate(values.passportExpiryDate)) {
        errors.passportExpiryDate = `Your ${
          values.identityType.value === IdentityTypeDirectors.PASSPORT ? 'passport' : 'document'
        } must be valid for a minimum of 6 months.`;
      }
    }

    if (!isValidName(stripWhitespace(values.name))) {
      errors.name = 'The name entered is invalid';
    }

    if (!isValidName(stripWhitespace(values.surname))) {
      errors.surname = 'The surname entered is invalid';
    }

    const emailTradeRootValidationError = validateEmailTradeRoot(values.emailAddress);

    if (emailTradeRootValidationError) {
      errors.emailAddress = emailTradeRootValidationError;
    } else if (!isValidEmail(values.emailAddress)) {
      errors.emailAddress = 'The email address entered is invalid';
    }

    if (!isValidMobileNumber(stripWhitespace(values.mobileNumber))) {
      errors.mobileNumber = 'The mobile number entered is invalid';
    }

    if (creditVettingEnabled) {
      if (!values.title) {
        errors.title = 'Please select a valid title';
      }

      if (!values.initials) {
        errors.initials = 'The initials entered are invalid';
      }

      if (!values.maritalStatus) {
        errors.maritalStatus = 'Please select a valid marital status';
      }
    }

    if (
      creditVettingEnabled &&
      values.identityType.value !== IdentityTypeDirectors.RSA_ID &&
      !values.gender
    ) {
      errors.gender = 'Please select a gender';
    }

    setNextButtonEnabled(Object.values(errors).length === 0);

    return errors;
  };

  const { setNextButtonEnabled, setOnNextEventHandler, setLoadingOverlayVisible } = useWizardEffect(
    () => ({
      nextButtonEnabled: !isAnyFormErrors(getInitialValues()),
    }),
    [application],
  );

  useEffect(() => {
    application.tapOnPhoneJourney
      ? OnboardingTealiumService.onPersonalDetailsToPStart()
      : OnboardingTealiumService.onPersonalDetailsStart(taggingJourney);
  }, []);

  const getInitialValues = (): PersonalDetailsForm => {
    const consumerType = application?.contact?.consumerType;

    const consumerTypeListItem =
      consumerType !== undefined
        ? CONSUMER_TYPE_LIST.find((listItem) => listItem.value === consumerType)
        : undefined;

    return {
      consumerType: consumerTypeListItem,
      name: application?.contact?.name || '',
      title: application?.contact?.title || '',
      initials: application?.contact?.initials || '',
      maritalStatus: application?.contact?.maritalStatus || '',
      surname:
        application.contact?.surname !== LAST_NAME_PLACEHOLDER
          ? application?.contact?.surname || ''
          : '',
      identityType: IDENTITY_TYPE_LIST.find(
        (type) =>
          type.value === (application.contact?.identityType || IdentityTypeDirectors.RSA_ID),
      ),
      idNumber: application?.contact?.idNumber || '',
      gender: application?.contact?.gender || '',
      emailAddress: application?.contact?.emailAddress || '',
      mobileNumber: application?.contact?.mobileNumber || '',
      dateOfBirth: application.contact?.dateOfBirth,
      countryOfOrigin: application.contact?.countryOfOrigin,
      passportExpiryDate: application.contact?.passportExpiryDate,
    } as PersonalDetailsForm;
  };

  const initialStatus = useMemo((): FormStatus | {} => {
    if (!application.pendingApp?.isPending) {
      return {
        initialDisabled: {
          name: isLocked(application.lockedFields, LOCK_NAMES.FIRST_NAMES),
          identityType: isLocked(application.lockedFields, LOCK_NAMES.ID_NUMBER),
          idNumber: isLocked(application.lockedFields, LOCK_NAMES.ID_NUMBER),
          emailAddress: isLocked(application.lockedFields, LOCK_NAMES.EMAIL),
          mobileNumber: isLocked(application.lockedFields, LOCK_NAMES.MOBILE_NUMBER),
        },
      };
    }

    if (!application.pendingApp.pageUpdates) {
      throw new Error('Personal details revamp: isPending set but no page updates available');
    }

    const allFields = getInitialValues();
    return getFormStatus(application.pendingApp.pageUpdates, allFields, PAGE_NAME.PERSONAL_DETAILS);
  }, [application?.pendingApp]);

  const submit = async (values: PersonalDetailsForm) => {
    const contact: Contact = {
      consumerType: values.consumerType?.value,
      name: values.name,
      surname: values.surname,
      identityType: values.identityType.value,
      idNumber: values.idNumber,
      emailAddress: values.emailAddress,
      mobileNumber: values.mobileNumber,
      title: values.title,
      initials: values.initials,
      maritalStatus: values.maritalStatus,
      dateOfBirth: values.dateOfBirth,
      passportExpiryDate: values.passportExpiryDate,
      gender: values.gender,
      countryOfOrigin: values.countryOfOrigin,
    };
    setLoadingOverlayVisible(true);
    const applicationNew = await dispatchAsync({
      type: ApplicationActionType.UPDATE_CONTACT_DETAILS,
      contactDetails: contact,
    });
    await salesforceService.saveApplication(
      getApplicationAPIFromApplication(applicationNew, catalogue),
    );
    application.tapOnPhoneJourney
      ? OnboardingTealiumService.onPersonalDetailsToPEnd()
      : OnboardingTealiumService.onPersonalDetailsEnd(values.identityType.value, taggingJourney);
    dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
    setLoadingOverlayVisible(false);
  };

  const getIdPlaceholder = (identityType: IdentityTypeDirectors) => {
    switch (identityType) {
      case IdentityTypeDirectors.RSA_ID:
        return 'RSA ID Number';

      case IdentityTypeDirectors.PASSPORT:
        return 'Passport number';

      case IdentityTypeDirectors.REFUGEE:
      case IdentityTypeDirectors.ASYLUM_SEEKER:
      default:
        return 'Identity number';
    }
  };

  const getExpiryDateLabel = (identitType: IdentityTypeDirectors) => {
    if (identitType === IdentityTypeDirectors.PASSPORT) {
      return 'Passport expiry date';
    }

    return 'Document expiry date';
  };

  const getIdMask = (values: PersonalDetailsForm) => {
    if (values.identityType.value === IdentityTypeDirectors.RSA_ID) return idMask;
    switch (values.identityType.value) {
      case IdentityTypeDirectors.PASSPORT:
        return passportMask;
      case IdentityTypeDirectors.ASYLUM_SEEKER:
        return asylumMask;
      case IdentityTypeDirectors.REFUGEE:
        return refugeeMask;
      default:
        spaceBy4Mask;
    }
    return spaceBy4Mask;
  };

  const excludedFields = () =>
    creditVettingEnabled ? [] : ['title', 'initials', 'maritalStatus', 'gender'];

  return (
    <article className="personal-details">
      <PageLayout>
        <UnderAgeModal
          visible={isUnderAge}
          onClose={() => {
            setUnderAge(false);
            OnboardingTealiumService.onUnderageModalEnd(taggingJourney);
          }}
          onExitApplication={async () => {
            OnboardingTealiumService.onUnderageModalExitAction(taggingJourney);
            const applicationNew = await dispatchAsync({
              type: ApplicationActionType.KILL_APPLICATION,
            });
            await salesforceService.saveApplication(killApplication(applicationNew, catalogue));
          }}
        />
        <HeadingLayout
          renderMainHeading={() => <HeadingOne>{headings.main}</HeadingOne>}
          renderSubHeading={() => <HeadingTwo>{headings.sub}</HeadingTwo>}
        />
        <ScrollTop />

        <Formik<PersonalDetailsForm>
          innerRef={(formik) => formik && setOnNextEventHandler(formik.submitForm)}
          initialValues={getInitialValues()}
          initialStatus={initialStatus}
          validate={validate}
          onSubmit={submit}
          validateOnChange={true}
          enableReinitialize={true}
        >
          {({ values, setFieldValue }) => (
            <Form>
              {initFieldCount(values, excludedFields())}
              {application.tapOnPhoneJourney &&
                sendIdleEvent<PersonalDetailsForm>(
                  values,
                  OnboardingTealiumService.onPersonalDetailsToPIdle,
                  excludedFields(),
                )}
              <FormikChangeListener<PersonalDetailsForm>>
                {(oldValues, newValues) => {
                  if (oldValues.identityType !== newValues.identityType) {
                    setFieldValue('idNumber', '');
                  }
                }}
              </FormikChangeListener>
              <FormLayout>
                <Field
                  name="consumerType"
                  type="text"
                  placeholder={placeholder}
                  fieldType="select"
                  items={CONSUMER_TYPE_LIST}
                  getItemDisplayValue={(item) => item.label}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  automationid="Revamp_Personal_Details_Consumer_Type"
                  className="light top-field"
                  autoFocus={!isMobile}
                  pageName={pageName}
                />
                {creditVettingEnabled && (
                  <FormRowLayout>
                    {[
                      () => (
                        <Field
                          name="title"
                          type="text"
                          placeholder="Title"
                          component={MiniAppFocusWrapper}
                          innerComponent={InputField}
                          fieldType="select"
                          items={Object.values(CREDIT_VETTING_TITLE)}
                          getItemDisplayValue={(item: string) => item}
                          className="light"
                          isComboField={true}
                          automationid="Revamp_Personal_Details_Title"
                          pageName={pageName}
                        />
                      ),
                      () => (
                        <Field
                          name="initials"
                          type="text"
                          placeholder="Initials"
                          component={MiniAppFocusWrapper}
                          innerComponent={InputField}
                          fieldType="standard"
                          className="light"
                          isComboField={true}
                          automationid="Revamp_Personal_Details_Initials"
                          pageName={pageName}
                        />
                      ),
                    ]}
                  </FormRowLayout>
                )}
                <Field
                  name="name"
                  placeholder="Name"
                  maxLength={FIRST_NAME_MAX_LENGTH}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="standard"
                  automationid="Revamp_Personal_Details_Name"
                  className="light"
                  pageName={pageName}
                />
                <Field
                  name="surname"
                  placeholder="Surname"
                  maxLength={SURNAME_MAX_LENGTH}
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="standard"
                  automationid="Revamp_Personal_Details_Surname"
                  className="light"
                  pageName={pageName}
                />
                <Field
                  name="emailAddress"
                  placeholder="Email address"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="standard"
                  automationid="Revamp_Personal_Details_Email_Address"
                  className="light"
                  pageName={pageName}
                />
                <Field
                  className="light"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="masked"
                  mask={phoneNumberMaskSpaced}
                  name="mobileNumber"
                  placeholder="Mobile number"
                  type="tel"
                  automationid="Revamp_Personal_Details_Mobile_Number"
                  pageName={pageName}
                />

                {isForeignNationalsEnabled && (
                  <Field
                    name="identityType"
                    type="text"
                    placeholder="Identity Type"
                    fieldType="select"
                    items={IDENTITY_TYPE_LIST}
                    getItemDisplayValue={(item) => item.label}
                    component={MiniAppFocusWrapper}
                    innerComponent={InputField}
                    automationid="Revamp_Personal_Details_Consumer_Type"
                    className="light"
                    autoFocus={!isMobile}
                    pageName={pageName}
                  />
                )}

                <Field
                  className="top-field light"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="masked"
                  mask={getIdMask(values)}
                  guide={!inMiniApp && values.identityType.value === IdentityTypeDirectors.RSA_ID}
                  name="idNumber"
                  placeholder={getIdPlaceholder(values.identityType.value)}
                  type={values.identityType.value === IdentityTypeDirectors.RSA_ID ? 'tel' : 'text'}
                  automationid="Revamp_Personal_Details_RSA_ID_Number"
                  pageName={pageName}
                />

                {creditVettingEnabled &&
                  values.identityType.value !== IdentityTypeDirectors.RSA_ID && (
                    <Field
                      name="gender"
                      type="text"
                      placeholder="Gender"
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      items={['Male', 'Female']}
                      getItemDisplayValue={(item) => item}
                      fieldType="select"
                    />
                  )}

                {values.identityType.value !== IdentityTypeDirectors.RSA_ID && (
                  <Field
                    name="countryOfOrigin"
                    type="text"
                    placeholder="Country of origin"
                    items={COUNTRY_CODES}
                    getItemDisplayValue={(item) => item}
                    component={MiniAppFocusWrapper}
                    innerComponent={InputField}
                    fieldType="select"
                    isDark={false}
                  />
                )}

                {values.identityType.value !== IdentityTypeDirectors.RSA_ID && (
                  <FormRowLayout>
                    {[
                      () => (
                        <Field
                          name="passportExpiryDate"
                          fieldName={getExpiryDateLabel(values.identityType.value)}
                          component={MiniAppFocusWrapper}
                          innerComponent={StyledDatePicker}
                          lightMode={true}
                        />
                      ),
                      () => (
                        <Field
                          name="dateOfBirth"
                          fieldName="Date of birth"
                          component={MiniAppFocusWrapper}
                          innerComponent={StyledDatePicker}
                          isLightMode={true}
                          minDate={MIN_DATE_ALLOWED}
                          maxDate={new Date()}
                        />
                      ),
                    ]}
                  </FormRowLayout>
                )}

                {creditVettingEnabled && (
                  <Field
                    name="maritalStatus"
                    type="text"
                    placeholder="Marital status"
                    items={Object.values(MARITAL_STATUSES)}
                    getItemDisplayValue={(item: string) => item}
                    component={MiniAppFocusWrapper}
                    innerComponent={InputField}
                    fieldType="select"
                    className="light bottom-field"
                    automationid="Revamp_Personal_Details_Marital_Status"
                    pageName={pageName}
                  />
                )}
              </FormLayout>
            </Form>
          )}
        </Formik>
      </PageLayout>
      <HelpButton
        onOpen={() =>
          OnboardingTealiumService.onHelpButtonActions(
            PAGE_NAME.PERSONAL_DETAILS,
            'help',
            taggingJourney,
          )
        }
        onEmailClicked={() =>
          OnboardingTealiumService.onHelpButtonActions(
            PAGE_NAME.PERSONAL_DETAILS,
            'email support',
            taggingJourney,
          )
        }
        onPhoneNumberClicked={() =>
          OnboardingTealiumService.onHelpButtonActions(
            PAGE_NAME.PERSONAL_DETAILS,
            'call support number',
            taggingJourney,
          )
        }
      />
    </article>
  );
};
